/**
 * TwilioSetupModal — configure Twilio credentials for SMS and voice paging.
 * Enter credentials, optionally send a test SMS, then Save.
 */
import { useState } from 'react'
import { X, CheckCircle, AlertCircle, Loader2, Send, Copy, Check, PhoneCall } from 'lucide-react'
import {
  saveTwilioConfig,
  testTwilioConfig,
  deleteTwilioConfig,
  type TwilioConfigStatus,
} from '../api/twilio_config'

interface TwilioSetupModalProps {
  onClose: () => void
  onSaved: (status: TwilioConfigStatus) => void
  existing?: TwilioConfigStatus
}

export function TwilioSetupModal({ onClose, onSaved, existing }: TwilioSetupModalProps) {
  const [accountSid, setAccountSid] = useState(existing?.account_sid ?? '')
  const [authToken, setAuthToken] = useState('')
  const [fromNumber, setFromNumber] = useState(existing?.from_number ?? '')
  const [messagingServiceSid, setMessagingServiceSid] = useState(existing?.messaging_service_sid ?? '')
  const [testTo, setTestTo] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ account_name: string; message: string } | null>(null)
  const [webhookCopied, setWebhookCopied] = useState(false)

  async function handleTest() {
    setTesting(true)
    setError(null)
    setTestResult(null)
    try {
      const result = await testTwilioConfig({
        account_sid: accountSid.trim(),
        auth_token: authToken.trim(),
        from_number: fromNumber.trim(),
        messaging_service_sid: messagingServiceSid.trim(),
        test_to: testTo.trim() || undefined,
      })
      setTestResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const status = await saveTwilioConfig({
        account_sid: accountSid.trim(),
        auth_token: authToken.trim(),
        from_number: fromNumber.trim(),
        messaging_service_sid: messagingServiceSid.trim(),
      })
      onSaved(status)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true)
    try {
      await deleteTwilioConfig()
      onSaved({ configured: false, has_auth_token: false })
      onClose()
    } catch {
      setError('Failed to disconnect')
    } finally {
      setDisconnecting(false)
    }
  }

  function copyWebhookUrl() {
    if (!existing?.sms_webhook_url) return
    navigator.clipboard.writeText(existing.sms_webhook_url)
    setWebhookCopied(true)
    setTimeout(() => setWebhookCopied(false), 2000)
  }

  const inputClass =
    'w-full h-9 rounded-lg border border-border bg-white px-3 text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary/30'
  const hintClass = 'text-xs text-text-tertiary mt-1'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F22F46]/10 text-[#F22F46]">
              <PhoneCall className="w-3.5 h-3.5" />
            </div>
            <h2 className="text-sm font-semibold text-text-primary">Connect Twilio</h2>
          </div>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Environment credentials note */}
          {existing?.source === 'environment' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
              <p className="font-medium">Credentials are coming from TWILIO_* environment variables</p>
              <p className="mt-0.5">
                Saving here stores your own credentials and overrides the environment values.
              </p>
            </div>
          )}

          {/* Setup notes */}
          <div className="bg-surface-secondary rounded-lg p-4 text-xs text-text-secondary space-y-1">
            <p className="font-medium text-text-primary mb-2">Setup notes</p>
            <p>
              Regen must be reachable at a public <code className="bg-white px-1 rounded border border-border">APP_URL</code>{' '}
              for voice-answer callbacks.
            </p>
            {existing?.sms_webhook_url ? (
              <>
                <p>
                  Paste this URL into the Messaging webhook on your Twilio number so SMS
                  ACK/RESOLVE replies work:
                </p>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 bg-white border border-border rounded px-2 py-1 text-text-secondary font-mono truncate">
                    {existing.sms_webhook_url}
                  </code>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex-shrink-0 p-1 rounded hover:bg-white transition-colors"
                    title="Copy webhook URL"
                  >
                    {webhookCopied
                      ? <Check className="w-3 h-3 text-green-600" />
                      : <Copy className="w-3 h-3 text-text-tertiary" />
                    }
                  </button>
                </div>
              </>
            ) : (
              <p>Set APP_URL so Regen can expose the SMS webhook URL for your Twilio number.</p>
            )}
            <p>
              Responders need a Phone number set in Settings → Users, and escalation tiers
              choose SMS or Voice under "Notify via".
            </p>
          </div>

          {/* Account SID */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Account SID</label>
            <input
              type="text"
              value={accountSid}
              onChange={(e) => setAccountSid(e.target.value)}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
          </div>

          {/* Auth token */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Auth Token</label>
            <input
              type="password"
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder={existing?.has_auth_token ? 'Leave blank to keep saved token' : 'Your Twilio auth token'}
              className={inputClass}
            />
          </div>

          {/* From number */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">From number</label>
            <input
              type="text"
              value={fromNumber}
              onChange={(e) => setFromNumber(e.target.value)}
              placeholder="+14155550132"
              className={inputClass}
            />
            <p className={hintClass}>E.164 e.g. +14155550132 — a Twilio number you own</p>
          </div>

          {/* Messaging Service SID */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Messaging Service SID <span className="text-text-tertiary font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={messagingServiceSid}
              onChange={(e) => setMessagingServiceSid(e.target.value)}
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputClass}
            />
            <p className={hintClass}>Preferred for SMS when set</p>
          </div>

          {/* Test phone number */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Test phone number <span className="text-text-tertiary font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+14155550132"
              className={inputClass}
            />
            <p className={hintClass}>Send a test SMS to this number</p>
          </div>

          {testResult && (
            <div className="flex items-start gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>{testResult.account_name}</strong> — {testResult.message}
              </span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex-1 h-9 rounded-lg border border-border text-sm text-text-secondary hover:bg-surface-secondary disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 h-9 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-hover disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {/* Disconnect (only for database-stored credentials) */}
          {existing?.configured && existing.source === 'database' && (
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="w-full text-xs text-red-500 hover:text-red-600 text-center py-1"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect Twilio'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
